import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useSiteLocations } from '@hooks/useSiteLocations';
import { FloorSvg } from '@components/FloorSvg/FloorSvg';
import { ScrollArea } from '@components/ui/ScrollArea/ScrollArea';
import { SearchInput } from '@components/ui/SearchInput/SearchInput';
import { NoMatches } from '../NoMatches';
import { fetchFloorViewSvg } from '@apis/storage';
import { fold } from '../../units';
import { useLogisticsEditLock } from '../../revision/LogisticsEditLock';
import { compareFloorNames } from './siteMap.logic';
import styles from './PlanPicker.module.css';

export interface IPlanPickerProps {
  siteId: string;
  /** Floors that already carry a saved map — every plan of the site holding
   *  an area or equipment, from the site-map read. */
  plansWithMaps: string[];
  /** Fires once, when "Use this plan" is clicked for the floor currently
   *  highlighted in the list — never on a bare row click. The caller decides
   *  what that means (first-run: commit directly; change-plan: stage the
   *  switch confirm). */
  onPick: (floorId: string) => void;
  /** True while the panel is the fixed content-area overlay (threaded from
   *  `SiteMap`'s own `expanded` prop) — makes the picker flex-fill the
   *  overlay's available height (rail list scrolls, preview fills the rest,
   *  footer stays pinned) instead of keeping its in-flow fixed height. Only
   *  meaningful for the embedded first-run picker — the ⋯ "Change base
   *  plan…" surface is a centered modal with its own fixed sizing regardless
   *  of the card's expanded state, so that call site omits this. */
  expanded?: boolean;
  /** The caller's role may write: without it browsing and previewing floors
   *  stays live, but "Use this plan" (the one write) is not rendered. */
  canEdit: boolean;
  /** The pick's write is in flight (first run commits on click): "Use this
   *  plan" locks so a second click can't send it twice. */
  busy?: boolean;
}

/**
 * Master–detail base-plan chooser (spec: Option A). Left rail is a real,
 * searchable, natural-sorted floor list; the right pane renders the
 * highlighted floor's blueprint live via the shared `FloorSvg`, so the user
 * sees the actual plan before committing. One component serves both the
 * first-run empty state and the ⋯ "Change base plan…" dialog — the two
 * surfaces differ only in what `onPick` does with the chosen floor.
 */
export function PlanPicker({
  siteId,
  plansWithMaps,
  onPick,
  expanded = false,
  canEdit,
  busy = false,
}: IPlanPickerProps) {
  const { t } = useTranslation();
  const { locked } = useLogisticsEditLock();
  const { floors } = useSiteLocations(siteId);
  const hasMaps = new Set(plansWithMaps);
  const [query, setQuery] = useState('');
  // Nothing is highlighted until the user picks a row — matches the approved
  // mock's empty preview prompt, for both surfaces this component serves.
  const [selectedFloorId, setSelectedFloorId] = useState<string | null>(null);

  const sortedFloors = useMemo(() => [...floors].sort((a, b) => compareFloorNames(a.name, b.name)), [floors]);
  const visibleFloors = useMemo(() => {
    // Folded like every logistics search — full-width input still matches.
    const q = fold(query.trim());
    if (!q) return sortedFloors;
    return sortedFloors.filter((floor) => fold(floor.name).includes(q));
  }, [sortedFloors, query]);

  const selectedFloor = sortedFloors.find((floor) => floor.id === selectedFloorId) ?? null;

  // Subscribes to the SAME floor-view query `<FloorSvg>` itself runs (shared
  // `queryKey` — TanStack Query dedupes, so this doesn't add a second
  // request) purely to observe the fetch's OUTCOME. A 404 (no blueprint ever
  // uploaded for this floor) resolves to `null`, which `FloorSvg` alone only
  // ever renders as a silent blank pane — this turns that into an explicit
  // message and gates "Use this plan" on it, since a map can't be drawn on a
  // floor with no blueprint.
  const previewQuery = useQuery({
    queryKey: ['storage', 'floor-view', siteId, selectedFloor?.id ?? ''],
    queryFn: ({ signal }) => fetchFloorViewSvg(siteId, selectedFloor!.id, signal),
    enabled: Boolean(selectedFloor),
    staleTime: 5 * 60_000,
  });
  const previewLoading = Boolean(selectedFloor) && previewQuery.isLoading;
  // Resolved (not loading) with no plan — kept distinct from "still loading"
  // so the message never flashes while the fetch is in flight; that gap
  // stays visually neutral (FloorSvg renders its own loading treatment).
  const noBlueprint = Boolean(selectedFloor) && !previewLoading && previewQuery.data == null;

  return (
    <div className={styles.picker} data-expanded={expanded || undefined}>
      <div className={styles.rail}>
        <div className={styles.railTitle}>
          {t('logistics.siteMap.emptyTitle')}
          <small>{t('logistics.siteMap.emptySubtitle')}</small>
        </div>
        <SearchInput
          value={query}
          onChange={setQuery}
          placeholder={t('logistics.siteMap.searchFloors')}
          ariaLabelClear={t('logistics.materials.searchClear')}
          className={styles.search}
        />
        {/* Only a filter MISS gets the message — an unfiltered empty floor
            list (locations still loading / none exist) stays silent rather
            than claiming a search found nothing. */}
        {query.trim() !== '' && visibleFloors.length === 0 && <NoMatches />}
        <ScrollArea className={styles.listScroll}>
          <ul className={styles.list}>
            {visibleFloors.map((floor) => {
              const selected = floor.id === selectedFloorId;
              const saved = hasMaps.has(floor.id);
              return (
                <li key={floor.id}>
                  <button
                    type="button"
                    className={styles.row}
                    data-selected={selected || undefined}
                    onClick={() => setSelectedFloorId(floor.id)}
                  >
                    <span className={styles.rowName}>{floor.name}</span>
                    <span className={saved ? `${styles.tag} ${styles.tagSaved}` : styles.tag}>
                      {saved ? t('logistics.siteMap.hasSavedMap') : t('logistics.siteMap.emptyPlan')}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </ScrollArea>
      </div>
      <div className={styles.preview}>
        <div className={styles.previewFrame}>
          {!selectedFloor ? (
            <p className={styles.previewPrompt}>{t('logistics.siteMap.selectFloorPrompt')}</p>
          ) : noBlueprint ? (
            <p className={styles.previewPrompt}>{t('logistics.siteMap.noBlueprint')}</p>
          ) : (
            <FloorSvg siteId={siteId} floorId={selectedFloor.id} />
          )}
        </div>
        <div className={styles.previewBar}>
          <span className={styles.previewName}>{selectedFloor?.name ?? '—'}</span>
          <span className={styles.spacer} />
          {canEdit ? (
            <button
              type="button"
              className={styles.useBtn}
              disabled={!selectedFloor || previewLoading || noBlueprint || locked || busy}
              onClick={() => {
                if (selectedFloor) onPick(selectedFloor.id);
              }}
            >
              {t('logistics.siteMap.usePlan')}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
